import React, { useState } from "react";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import ProgressBar from "react-bootstrap/ProgressBar";
import { Trans, useTranslation } from "react-i18next";
import PriestBubble from "./PriestBubble";
import UserBubble from "./UserBubble";
import "./WalkthroughInteractivo.scss";

const WalkthroughInteractivo = ({ selectedSins, customSins, onFinish, onBack }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);

  // Combinar pecados predefinidos y personalizados
  const allSins = [...selectedSins, ...customSins.map((text) => ({ text }))];

  // Definir pasos del walkthrough
  const steps = [
    // Paso 0: Bienvenida
    {
      type: "intro",
      content: "intro"
    },
    // Paso 1: Inicio de la confesión
    {
      type: "dialogue",
      priest: "walkthrough.in_the_name_of",
      user: "walkthrough.bless_me_father"
    },
    // Paso 2: Lista de pecados (todos juntos)
    {
      type: "sins_list",
      title: "walkthrough_interactive.my_sins"
    },
    // Paso 3: Cierre de la confesión de pecados
    {
      type: "dialogue",
      user: "walkthrough.these_are_my_sins"
    },
    // Paso 4: Consejos del sacerdote
    {
      type: "dialogue",
      priest: "walkthrough.your_confessor_may_offer",
      priest2: "walkthrough.your_confessor_will_assign"
    },
    // Paso 5: Acto de Contrición
    {
      type: "prayer",
      title: "prayers.act_of_contrition",
      prayerKey: "prayers.act_of_contrition_text"
    },
    // Paso 6: Absolución
    {
      type: "absolution",
      priest: "walkthrough.god_the_father_of_mercies",
      user: "walkthrough.amen",
      priest2: "walkthrough.the_lord_has_freed_you",
      user2: "walkthrough.thanks_be_to_god"
    },
    // Paso 7: Finalización
    {
      type: "outro",
      content: "outro"
    }
  ];

  const totalSteps = steps.length;
  const step = steps[currentStep];

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onFinish();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      onBack();
    }
  };

  const renderStep = () => {
    switch (step.type) {
      case "intro":
        return (
          <div className="step-intro">
            <div className="intro-icon">⛪</div>
            <h3>{t("walkthrough_interactive.ready_title")}</h3>
            <p className="text-muted">{t("walkthrough_interactive.ready_subtitle")}</p>
            <div className="sin-summary">
              <div className="summary-number">{allSins.length}</div>
              <div className="summary-text">{t("walkthrough_interactive.sins_to_confess")}</div>
            </div>
          </div>
        );

      case "dialogue":
        return (
          <div className="step-dialogue">
            {step.priest && <PriestBubble text={t(step.priest)} />}
            {step.user && <UserBubble>{t(step.user)}</UserBubble>}
            {step.priest2 && <PriestBubble text={t(step.priest2)} />}
            {step.user2 && <UserBubble>{t(step.user2)}</UserBubble>}
          </div>
        );

      case "sins_list":
        return (
          <div className="step-sins-list">
            <h4 className="text-center mb-4">{t(step.title)}</h4>
            <div className="sins-container">
              {allSins.map((sinItem, index) => (
                <div key={index} className="sin-bubble-item">
                  <span className="sin-number">{index + 1}</span>
                  <span>{sinItem.text}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case "prayer":
        return (
          <div className="step-prayer">
            <h4 className="text-center mb-4">{t(step.title)}</h4>
            <UserBubble className="prayer-bubble">
              <Trans t={t} i18nKey={step.prayerKey}>
                My God,
                <br />
                I am sorry for my sins with all my heart.
                <br />
                In choosing to do wrong and failing to do good, I have sinned
                against You, whom I should love above all things.
                <br />
                I firmly intend with Your help to do penance, to sin no more, and to
                avoid whatever leads me to sin.
                <br />
                Jesus Christ suffered and died for us. In His name, my God, have
                mercy.
              </Trans>
            </UserBubble>
          </div>
        );

      case "absolution":
        return (
          <div className="step-absolution">
            {step.priest && <PriestBubble text={t(step.priest)} />}
            {step.user && <UserBubble>{t(step.user)}</UserBubble>}
            {step.priest2 && <PriestBubble text={t(step.priest2)} absolution />}
            {step.user2 && <UserBubble>{t(step.user2)}</UserBubble>}
          </div>
        );

      case "outro":
        return (
          <div className="step-outro">
            <div className="outro-icon">✝️</div>
            <h3>{t("walkthrough_interactive.confession_complete")}</h3>
            <p className="text-muted">{t("walkthrough_interactive.go_in_peace")}</p>
          </div>
        );

      default:
        return null;
    }
  };

  const getStepTitle = () => {
    if (step.type === "intro") return t("walkthrough_interactive.step_begin");
    if (step.type === "outro") return t("walkthrough_interactive.step_complete");
    return t("walkthrough_interactive.step") + ` ${currentStep}/${totalSteps - 2}`;
  };

  return (
    <div className="walkthrough-interactive">
      <Container className="py-4">
        <div className="walkthrough-container">
          {/* Header con navegación */}
          <div className="walkthrough-header-interactive">
            <Button
              variant="link"
              className="back-btn p-0"
              onClick={handlePrevious}
            >
              ← {currentStep === 0 ? t("walkthrough_interactive.back") : t("walkthrough_interactive.previous")}
            </Button>
            <span className="step-title">{getStepTitle()}</span>
            <div style={{ width: "80px" }}></div>
          </div>

          {/* Barra de progreso */}
          <ProgressBar
            now={((currentStep + 1) / totalSteps) * 100}
            className="progress-bar-custom"
            variant="primary"
          />

          {/* Contenido del paso */}
          <Card className="walkthrough-card">
            <Card.Body className="walkthrough-body">
              {renderStep()}
            </Card.Body>
          </Card>

          {/* Botón de acción */}
          <div className="walkthrough-footer">
            <Button
              variant="primary"
              size="lg"
              className="next-btn"
              onClick={handleNext}
            >
              {currentStep === totalSteps - 1
                ? t("walkthrough_interactive.finish")
                : t("walkthrough_interactive.next")
              }
              <i className={`fa ${currentStep === totalSteps - 1 ? "fa-check" : "fa-arrow-right"} ms-2`}></i>
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default WalkthroughInteractivo;
